/** After the server refuses a form, move focus to what refused it - WCAG 3.3.1, Q-06 (G-P8-45).
 *
 * Measured on the deployed release: of seven forms, none moved focus after a refusal. It stayed on the
 * button that was pressed, or fell to the page, so a keyboard or screen-reader user heard nothing and
 * had to search for the sentence. This is the one rule for the forms that already put the server's
 * sentence on the screen: when `refusal` becomes set, focus the first field marked
 * `aria-invalid="true"` inside `root`, else the first `role="alert"` sentence (made focusable for the
 * purpose, so it is announced and the next Tab continues from it).
 *
 * `scope` is the element holding the form (`useFormChecks`' `element`, or any state-held element).
 * `refusal` is the thing that refused (an error object, or any value that changes with it): the effect
 * runs when it changes to something truthy, after the commit that rendered the sentence, so the
 * markers it looks for are already there.
 */

import { useEffect } from "react";

const FOCUSABLE = "input, select, textarea, button, a[href], [tabindex]";

export function useFocusRefusal(scope: HTMLElement | null, refusal: unknown): void {
  useEffect(() => {
    if (!refusal) return;
    if (!scope) return;
    const target =
      scope.querySelector<HTMLElement>('[aria-invalid="true"]') ??
      scope.querySelector<HTMLElement>('[role="alert"]');
    if (!target) return;
    if (!target.matches(FOCUSABLE)) target.setAttribute("tabindex", "-1");
    target.focus();
  }, [refusal, scope]);
}
